import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import Header from '../../../components/Header';
import api_service from '../../../network/api_callbacks';

const formatAnswerValue = (value) => {
  if (Array.isArray(value)) return value.join(', ');
  if (value === null || value === undefined || value === '') return '-';
  if (typeof value === 'object') {
    if (value.fileName) return value.fileName;
    if (value.dataUrl) return 'Uploaded file';
    return JSON.stringify(value);
  }
  return String(value);
};

export default function FeedbackResponseDetail() {
  const { id, responseId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [response, setResponse] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const result = await api_service.getFeedbackResponseDetail({ id, responseId });
        if (cancelled) return;
        if (result?.responseCode === 200) {
          setForm(result.data?.form || null);
          setResponse(result.data?.response || null);
        } else {
          toast.error(result?.error?.message || 'Response not found');
          navigate(`/feedback-forms/${id}/responses`);
        }
      } catch (err) {
        toast.error(err?.message || 'Failed to load response');
        navigate(`/feedback-forms/${id}/responses`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id, responseId, navigate]);

  const fieldLabelMap = useMemo(() => {
    const map = {};
    (form?.fields || []).forEach((field) => {
      map[field.id] = field.label;
    });
    return map;
  }, [form]);

  const orderedAnswers = useMemo(() => {
    const answers = response?.answers || [];
    const fieldOrder = (form?.fields || [])
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((field) => field.id);

    return answers.slice().sort((a, b) => {
      const ai = fieldOrder.indexOf(a.fieldId);
      const bi = fieldOrder.indexOf(b.fieldId);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  }, [response, form]);

  if (loading) {
    return (
      <Box sx={{ m: '20px', mt: '100px', textAlign: 'left' }} display="flex" justifyContent="center">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ m: '20px', mt: '100px', textAlign: 'left' }}>
      <Header
        title="Response Detail"
        subtitle={form ? form.title : 'Feedback response'}
      />

      <Box
        display="flex"
        flexWrap="wrap"
        gap={1.5}
        alignItems="center"
        mb={3}
        mt={2}
        p={2}
        sx={{
          bgcolor: '#f5f5f5',
          borderRadius: 1,
          border: '1px solid #e0e0e0',
        }}
      >
        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigate(`/feedback-forms/${id}/responses`)}
        >
          Back
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigate(`/feedback-forms/${id}`)}
        >
          Edit form
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => navigate('/feedback-forms')}
        >
          All forms
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Submitter
        </Typography>
        <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '1fr 1fr' }} gap={2}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Email
            </Typography>
            <Typography>{response?.email || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Name
            </Typography>
            <Typography>{response?.name || '-'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              User ID
            </Typography>
            <Typography>{response?.userId || 'Guest'}</Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Submitted at
            </Typography>
            <Typography>
              {response?.created_at ? new Date(response.created_at).toLocaleString() : '-'}
            </Typography>
          </Box>
        </Box>
        {response?.meta ? (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary" display="block">
              IP: {response.meta.ip || '-'}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              User agent: {response.meta.userAgent || '-'}
            </Typography>
          </>
        ) : null}
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Answers</Typography>
          {form?.status ? <Chip size="small" label={form.status} /> : null}
        </Box>
        {orderedAnswers.length === 0 ? (
          <Typography color="text.secondary">No answers recorded.</Typography>
        ) : (
          orderedAnswers.map((answer) => (
            <Box key={answer.fieldId} mb={2.5}>
              <Typography variant="caption" color="text.secondary" display="block">
                {fieldLabelMap[answer.fieldId] || answer.fieldId}
              </Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {formatAnswerValue(answer.value)}
              </Typography>
              <Divider sx={{ mt: 1.5 }} />
            </Box>
          ))
        )}
      </Paper>
    </Box>
  );
}
